import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Modal, Table, Tag, Tooltip, Select } from 'antd';
import { toast } from '@rickylandino/react-messages';
import { CheckCircle, AlertCircle } from 'lucide-react';
import Globals from '../../../../config/globals';
import { getResultEntryFailureCandidates } from '../../../../common/services/next-gen/WidgetDataService';
import { useReconcileResultEntryFailure } from '../../../../hooks/useWidgetData';
import { getCategoryFacilityData } from '../../../../common/services/FacilityService';

const { Option } = Select;

const EMPTY_OVERRIDES = { radiologistIdOverride: null, densityIdOverride: null, recommendationIdOverride: null, biradsOverride: null };

const isDark = () => Globals.theme === 'dark';

/**
 * ReconcileResultEntryFailureModal
 * Lets the user match a result entry failure to an existing patient exam.
 *
 * @param {Object} props
 * @param {boolean} props.visible
 * @param {Object|null} props.failure - The result entry failure record
 * @param {Function} props.onSuccess - Called after a successful reconcile
 * @param {Function} props.onCancel
 */
export default function ReconcileResultEntryFailureModal({ visible, failure, onSuccess, onCancel }) {
    const reconcileMutation = useReconcileResultEntryFailure();

    const [candidates, setCandidates] = useState([]);
    const [isLoadingCandidates, setIsLoadingCandidates] = useState(false);
    const [selectedExam, setSelectedExam] = useState(null);
    const [page, setPage] = useState(1);
    const [pageSize] = useState(50);
    const [totalCount, setTotalCount] = useState(0);

    // Supporting data (sourced from getCategoryFacilityData — same as PatientExamPane)
    const [radiologistList, setRadiologistList] = useState([]);
    const [densityList, setDensityList] = useState([]);
    const [recommendationList, setRecommendationList] = useState([]);
    const [biradList, setBiradList] = useState([]);
    const [biradRecommendations, setBiradRecommendations] = useState({});
    const [isLoadingSupporting, setIsLoadingSupporting] = useState(false);
    const supportingDataFacilityId = useRef(null);

    // Overrides the user selects in the corrections table (pre-populated from failure DTO on open)
    const [overrides, setOverrides] = useState(EMPTY_OVERRIDES);

    const isAmendment = failure?.reportState === 'A';
    const isFinal = failure?.reportState === 'F';

    const missingFields = failure ? [
        (!failure.reportState || !['F', 'A'].includes(failure.reportState)) && 'report state',
        !failure.facilityName && 'facility',
        (!failure.patientName && !failure.patientMrn) && 'patient identity',
    ].filter(Boolean) : [];
    const isUnactionable = missingFields.length > 0;

    useEffect(() => {
        if (!visible || !failure) return;
        setSelectedExam(null);
        setCandidates([]);
        setPage(1);
        setOverrides({
            radiologistIdOverride: failure.radiologistId ?? null,
            densityIdOverride: failure.densityId ?? null,
            recommendationIdOverride: failure.recommendationId ?? null,
            biradsOverride: failure.birads ?? null,
        });
        supportingDataFacilityId.current = null;
        loadCandidates(1);
        if (failure.facilityId) {
            loadSupportingData(failure.facilityId);
        }
    }, [visible, failure?.resultEntryFailureId]); // eslint-disable-line react-hooks/exhaustive-deps

    const loadSupportingData = async (facilityId) => {
        if (!facilityId || supportingDataFacilityId.current === facilityId) return;
        supportingDataFacilityId.current = facilityId;
        setIsLoadingSupporting(true);
        try {
            const categoryData = await getCategoryFacilityData(facilityId);
            setRadiologistList(categoryData.radiologistList || []);
            setDensityList(categoryData.densityList || []);
            setRecommendationList(categoryData.recommendationList || []);
            setBiradList(categoryData.biradList || []);
            setBiradRecommendations(categoryData.biradRecommendations || {});
        } catch (e) {
            console.error('Error loading reconcile supporting data:', e);
        } finally {
            setIsLoadingSupporting(false);
        }
    };

    const loadCandidates = async (pageNum) => {
        if (!failure?.resultEntryFailureId) return;
        setIsLoadingCandidates(true);
        try {
            const result = await getResultEntryFailureCandidates(failure.resultEntryFailureId, pageNum, pageSize);
            const rows = result?.data || result || [];
            setTotalCount(result?.totalCount ?? rows.length);

            // For amendments: sort isStudyDateMatch=true to top
            if (isAmendment) {
                rows.sort((a, b) => (b.isStudyDateMatch ? 1 : 0) - (a.isStudyDateMatch ? 1 : 0));
            }
            setCandidates(rows);
        } catch (e) {
            console.error('Error loading reconciliation candidates:', e);
            setCandidates([]);
        } finally {
            setIsLoadingCandidates(false);
        }
    };

    const formatDate = useCallback((dateString) => {
        if (!dateString) return '—';
        const s = String(dateString);
        // HL7 DTM: YYYYMMDDHHMMSS±HHMM (only the date portion is required)
        const hl7 = s.match(/^(\d{4})(\d{2})(\d{2})(\d{2})?(\d{2})?(\d{2})?([+-]\d{4})?$/);
        if (hl7) {
            const [, yr, mo, dy] = hl7;
            return new Date(Number(yr), Number(mo) - 1, Number(dy)).toLocaleDateString('en-US', {
                month: 'numeric', day: 'numeric', year: 'numeric'
            });
        }
        return new Date(dateString).toLocaleDateString('en-US', {
            month: 'numeric', day: 'numeric', year: 'numeric'
        });
    }, []);

    const pink = isDark() ? '#ec4899' : '#db2777';
    const pinkHover = isDark() ? '#db2777' : '#be185d';

    const columns = useMemo(() => {
        const base = [
            {
                title: 'Patient',
                key: 'patient',
                width: 160,
                render: (_, row) => (
                    <div>
                        <div className="font-medium">{row.patientName || '—'}</div>
                        {row.mrn && <div className="text-xs" style={{ color: isDark() ? '#a1a1aa' : '#71717a' }}>MRN: {row.mrn}</div>}
                        {row.patientDob && <div className="text-xs" style={{ color: isDark() ? '#a1a1aa' : '#71717a' }}>DOB: {formatDate(row.patientDob)}</div>}
                    </div>
                )
            },
            {
                title: 'Facility',
                dataIndex: 'facilityName',
                key: 'facilityName',
                width: 140,
                render: (val) => val || '—'
            },
            {
                title: 'Exam Type',
                dataIndex: 'examType',
                key: 'examType',
                width: 160,
                render: (val) => val || '—'
            },
            {
                title: 'Exam Date',
                dataIndex: 'examDateTime',
                key: 'examDateTime',
                width: 110,
                render: (d) => formatDate(d)
            },
            {
                title: 'Accession #',
                dataIndex: 'accessionNumber',
                key: 'accessionNumber',
                width: 130,
                render: (val) => val || '—'
            },
        ];

        if (isAmendment) {
            base.push({
                title: 'Status',
                key: 'status',
                width: 160,
                render: (_, row) => (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {row.isStudyDateMatch && (
                            <Tooltip title="Study date matches the message date">
                                <Tag color="blue" style={{ marginRight: 0, width: 'fit-content' }}>Date Match</Tag>
                            </Tooltip>
                        )}
                        {row.hasResult ? (
                            <Tooltip title="This exam already has a primary result — good candidate for an amendment">
                                <Tag color="green" style={{ marginRight: 0, width: 'fit-content' }}>Has Result</Tag>
                            </Tooltip>
                        ) : (
                            <Tag color="default" style={{ marginRight: 0, width: 'fit-content' }}>No Result</Tag>
                        )}
                    </div>
                )
            });
        }

        return base;
    }, [formatDate, isAmendment]);

    // Filter recommendations by the currently selected BIRADS, matching PrimaryResultCard behaviour.
    // Falls back to the full recommendationList when no BIRADS is selected or no mapping exists.
    const filteredRecommendationList = useMemo(() => {
        const selectedBirads = overrides.biradsOverride;
        if (selectedBirads && biradRecommendations[selectedBirads]?.length) {
            return biradRecommendations[selectedBirads];
        }
        return recommendationList;
    }, [overrides.biradsOverride, biradRecommendations, recommendationList]);

    // When facilityId is absent the supporting lists never load, so we cannot validate
    // against them. Bypass blocking entirely and let the API handle the values as-is.
    const canValidateAgainstFacilityLists = !!failure?.facilityId;

    const blockingFields = useMemo(() => ({
        radiologistIdOverride: canValidateAgainstFacilityLists && !!failure?.radiologistId &&
            !(overrides.radiologistIdOverride != null && radiologistList.some(r => r.radiologistID === overrides.radiologistIdOverride)),
        densityIdOverride: canValidateAgainstFacilityLists && !!(failure?.densityId || failure?.densityRaw) &&
            !(overrides.densityIdOverride != null && densityList.some(d => d.breastDensityID === overrides.densityIdOverride)),
        recommendationIdOverride: canValidateAgainstFacilityLists && !!(failure?.recommendationId || failure?.recommendationRaw) &&
            !(overrides.recommendationIdOverride != null && filteredRecommendationList.some(r => r.recommendationID === overrides.recommendationIdOverride)),
        biradsOverride: canValidateAgainstFacilityLists && !!failure?.birads &&
            !(overrides.biradsOverride != null && biradList.some(b => b.birads === overrides.biradsOverride)),
    }), [canValidateAgainstFacilityLists, failure, overrides, radiologistList, densityList, filteredRecommendationList, biradList]);

    const hasBlockingFields = Object.values(blockingFields).some(Boolean);

    const canReconcile = !!selectedExam && !reconcileMutation.isLoading && !isUnactionable && !hasBlockingFields;

    const handleReconcile = useCallback(async () => {
        if (!canReconcile) return;
        try {
            await reconcileMutation.mutateAsync({
                resultEntryFailureId: failure.resultEntryFailureId,
                patientExamId: selectedExam.patientExamId,
                radiologistIdOverride: overrides.radiologistIdOverride,
                densityIdOverride: overrides.densityIdOverride,
                recommendationIdOverride: overrides.recommendationIdOverride,
                biradsOverride: overrides.biradsOverride,
            });
            toast.success('Result entry failure reconciled successfully');
            onSuccess();
        } catch (e) {
            const msg = e?.response?.data?.message || e?.response?.data || 'Failed to reconcile. Please try again.';
            console.error('Reconcile error:', e);
            toast.error(typeof msg === 'string' ? msg : 'Failed to reconcile. Please try again.');
        }
    }, [canReconcile, selectedExam, failure, reconcileMutation, onSuccess, overrides]);

    const reportStateBadge = (state) => {
        if (state === 'F') return <Tag color="blue">Final</Tag>;
        if (state === 'A') return <Tag color="purple">Amendment</Tag>;
        return <Tag color="default">{state || 'Unknown'}</Tag>;
    };

    const rowStyle = (row) => {
        if (selectedExam?.patientExamId === row.patientExamId) {
            return { backgroundColor: isDark() ? '#4a1d6b' : '#fdf4ff', cursor: 'pointer' };
        }
        if (isAmendment && row.isStudyDateMatch) {
            return { backgroundColor: isDark() ? '#1e3a5f' : '#eff6ff', cursor: 'pointer' };
        }
        return { cursor: 'pointer' };
    };

    if (!failure) return null;

    return (
        <Modal
            visible={visible}
            title={
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span>Match to Exam</span>
                    {reportStateBadge(failure.reportState)}
                </div>
            }
            onCancel={onCancel}
            width={900}
            destroyOnClose
            footer={[
                <button
                    key="cancel"
                    onClick={onCancel}
                    className="px-4 py-1.5 rounded-lg font-medium text-sm"
                    style={{
                        backgroundColor: 'transparent',
                        border: `1px solid ${isDark() ? '#52525b' : '#d1d5db'}`,
                        cursor: 'pointer',
                        marginRight: 8
                    }}
                >
                    Cancel
                </button>,
                <button
                    key="confirm"
                    onClick={handleReconcile}
                    disabled={!canReconcile}
                    className="px-4 py-1.5 rounded-lg font-medium text-sm"
                    style={{
                        backgroundColor: canReconcile ? pink : (isDark() ? '#52525b' : '#d1d5db'),
                        color: canReconcile ? 'white' : (isDark() ? '#a1a1aa' : '#9ca3af'),
                        border: 'none',
                        cursor: canReconcile ? 'pointer' : 'not-allowed'
                    }}
                    onMouseEnter={e => { if (canReconcile) e.currentTarget.style.backgroundColor = pinkHover; }}
                    onMouseLeave={e => { if (canReconcile) e.currentTarget.style.backgroundColor = pink; }}
                >
                    {reconcileMutation.isLoading ? 'Reconciling…' : 'Confirm Match'}
                </button>
            ]}
        >
            {/* Failure summary */}
            {failure && (
                <div
                    className="rounded-lg p-3 mb-4"
                    style={{ backgroundColor: isDark() ? '#27272a' : '#f4f4f5', fontSize: 13 }}
                >
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 24px' }}>
                        {failure.accessionNumber && <span><strong>Accession:</strong> {failure.accessionNumber}</span>}
                        {failure.patientName && <span><strong>Patient:</strong> {failure.patientName}</span>}
                        {failure.patientMrn && <span><strong>MRN:</strong> {failure.patientMrn}</span>}
                        {failure.facilityName && <span><strong>Facility:</strong> {failure.facilityName}</span>}
                        {failure.examTypeName && <span><strong>Exam:</strong> {failure.examTypeName}</span>}
                        {failure.radiologistName && <span><strong>Radiologist:</strong> {failure.radiologistName}</span>}
                        {failure.birads != null && <span><strong>BIRADs:</strong> {failure.birads}</span>}
                        {failure.failureReason && <span><strong>Reason:</strong> {failure.failureReason}</span>}
                    </div>
                </div>
            )}

            {/* Unactionable warning */}
            {isUnactionable && (
                <div
                    className="rounded-lg p-3 mb-4 flex items-center gap-2"
                    style={{ backgroundColor: isDark() ? '#431407' : '#fff7ed', color: isDark() ? '#fdba74' : '#c2410c' }}
                >
                    <AlertCircle size={16} />
                    <span style={{ fontSize: 13 }}>
                        Cannot reconcile — missing: <strong>{missingFields.join(', ')}</strong>. Please dismiss this failure instead.
                    </span>
                </div>
            )}

            {/* Amendment hint */}
            {isAmendment && !isUnactionable && (
                <div
                    className="rounded-lg p-3 mb-4 flex items-center gap-2"
                    style={{ backgroundColor: isDark() ? '#1e1b4b' : '#eef2ff', color: isDark() ? '#a5b4fc' : '#4338ca' }}
                >
                    <CheckCircle size={16} />
                    <span style={{ fontSize: 13 }}>
                        <strong>Amendment:</strong> Select the exam this result amends. Exams highlighted in blue share the same study date. Exams marked <strong>Has Result</strong> already have a primary result — these are the most likely candidates.
                    </span>
                </div>
            )}

            {/* Final hint */}
            {isFinal && !isUnactionable && (
                <div
                    className="rounded-lg p-3 mb-4 flex items-center gap-2"
                    style={{ backgroundColor: isDark() ? '#052e16' : '#f0fdf4', color: isDark() ? '#86efac' : '#15803d' }}
                >
                    <CheckCircle size={16} />
                    <span style={{ fontSize: 13 }}>
                        <strong>Final Result:</strong> Select the exam this result belongs to. Only exams without an existing result are shown.
                    </span>
                </div>
            )}

            {/* No candidates */}
            {!isLoadingCandidates && candidates.length === 0 && !isUnactionable && (
                <div className="text-center py-6" style={{ color: isDark() ? '#a1a1aa' : '#71717a' }}>
                    No matching exams found. You may need to dismiss this failure.
                </div>
            )}

            {selectedExam && (
                <div className="mt-3" style={{ fontSize: 13, color: isDark() ? '#a1a1aa' : '#6b7280' }}>
                    Selected: <strong style={{ color: isDark() ? '#e4e4e7' : '#18181b' }}>{selectedExam.patientName || selectedExam.mrn || 'Unknown patient'}</strong>
                    {selectedExam.examDateTime && <> — {formatDate(selectedExam.examDateTime)}</>}
                    {selectedExam.accessionNumber && <> — {selectedExam.accessionNumber}</>}
                </div>
            )}

            {/* Candidates table */}
            {!isUnactionable && (
                <Table
                    columns={columns}
                    dataSource={candidates}
                    rowKey="patientExamId"
                    loading={isLoadingCandidates}
                    size="small"
                    scroll={{ y: 360 }}
                    pagination={{
                        current: page,
                        pageSize,
                        total: totalCount,
                        onChange: (p) => { setPage(p); loadCandidates(p); },
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`
                    }}
                    onRow={(row) => ({
                        onClick: () => setSelectedExam(row),
                        style: rowStyle(row)
                    })}
                    rowClassName={(row) =>
                        selectedExam?.patientExamId === row.patientExamId ? 'ant-table-row-selected' : ''
                    }
                />
            )}

            {/* Corrections — shown after an exam is selected so user can review/adjust HL7 field mappings */}
            {selectedExam && !isLoadingSupporting && (
                <div style={{ marginTop: 16 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <p style={{ fontWeight: 600, margin: 0 }}>Field Corrections</p>
                    </div>
                    {!canValidateAgainstFacilityLists && (
                        <div
                            className="rounded-lg p-3 mb-3 flex items-center gap-2"
                            style={{ backgroundColor: isDark() ? '#431407' : '#fff7ed', color: isDark() ? '#fdba74' : '#c2410c', fontSize: 13 }}
                        >
                            <AlertCircle size={14} />
                            <span>Facility could not be identified — field corrections are unavailable. Values from the HL7 message will be applied as-is.</span>
                        </div>
                    )}
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                        <thead>
                            <tr style={{ borderBottom: `1px solid ${isDark() ? '#3f3f46' : '#e4e4e7'}` }}>
                                {['Field', 'Power Reader Exam Details', 'Correction'].map(h => (
                                    <th key={h} style={{
                                        padding: '6px 12px',
                                        textAlign: 'left',
                                        fontSize: '0.75rem',
                                        fontWeight: 600,
                                        color: isDark() ? '#a1a1aa' : '#71717a',
                                        background: isDark() ? '#27272a' : '#f9fafb'
                                    }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {[
                                {
                                    label: 'Radiologist',
                                    hl7Display: failure.radiologistName || null,
                                    overrideKey: 'radiologistIdOverride',
                                    validIds: new Set(radiologistList.map(r => r.radiologistID)),
                                    listOptions: radiologistList.map(r => (
                                        <Option key={r.radiologistID} value={r.radiologistID}>
                                            {`${r.fName} ${r.lName}`.trim()}
                                        </Option>
                                    ))
                                },
                                {
                                    label: 'Density',
                                    hl7Display: failure.densityName || failure.densityRaw || failure.densityId || null,
                                    overrideKey: 'densityIdOverride',
                                    validIds: new Set(densityList.map(d => d.breastDensityID)),
                                    listOptions: densityList.map(d => (
                                        <Option key={d.breastDensityID} value={d.breastDensityID}>
                                            {d.densityDescription}
                                        </Option>
                                    ))
                                },
                                {
                                    label: 'Recommendation',
                                    hl7Display: failure.recommendationName || failure.recommendationRaw || failure.recommendationId || null,
                                    overrideKey: 'recommendationIdOverride',
                                    validIds: new Set(filteredRecommendationList.map(r => r.recommendationID)),
                                    listOptions: filteredRecommendationList.map(r => (
                                        <Option key={r.recommendationID} value={r.recommendationID}>
                                            {r.description}
                                        </Option>
                                    ))
                                },
                                {
                                    label: 'BIRADS',
                                    hl7Display: failure.birads || null,
                                    overrideKey: 'biradsOverride',
                                    validIds: new Set(biradList.map(b => b.birads)),
                                    listOptions: biradList.map(b => (
                                        <Option key={b.birads} value={b.birads}>
                                            {b.birads}
                                        </Option>
                                    )),
                                    onChange: (v) => setOverrides(prev => ({
                                        ...prev,
                                        biradsOverride: v ?? null,
                                        // When BIRADS is cleared the filtered recommendation list resets to the
                                        // full facility list. If the current recommendation isn't in that list
                                        // it would silently block submission, so clear it here.
                                        recommendationIdOverride: v == null ? null : prev.recommendationIdOverride,
                                    }))
                                }
                            ].map(field => {
                                const val = overrides[field.overrideKey];
                                const selectValue = val != null && field.validIds.has(val) ? val : undefined;
                                const isBlocking = blockingFields[field.overrideKey];
                                return (
                                    <tr key={field.overrideKey} style={{ borderBottom: `1px solid ${isDark() ? '#3f3f46' : '#f4f4f5'}` }}>
                                        <td style={{ padding: '8px 12px', fontWeight: 500 }}>{field.label}</td>
                                        <td style={{ padding: '8px 12px' }}>
                                            {field.hl7Display
                                                ? <span style={{ color: isBlocking ? '#f59e0b' : (isDark() ? '#e4e4e7' : '#18181b'), fontFamily: isBlocking ? 'monospace' : 'inherit' }}>{field.hl7Display}</span>
                                                : <span style={{ color: isDark() ? '#52525b' : '#d1d5db' }}>—</span>
                                            }
                                        </td>
                                        <td style={{ padding: '8px 12px' }}>
                                            <Select
                                                value={selectValue}
                                                onChange={field.onChange ?? (v => setOverrides(prev => ({ ...prev, [field.overrideKey]: v ?? null })))}
                                                placeholder={isBlocking ? 'Select a value before confirming' : 'None'}
                                                style={{ width: '100%', borderRadius: 6, outline: isBlocking ? '2px solid #f59e0b' : 'none' }}
                                                allowClear
                                                showSearch
                                                optionFilterProp="children"
                                            >
                                                {field.listOptions}
                                            </Select>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </Modal>
    );
}

